import { FontAwesome } from '@expo/vector-icons';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import * as React from 'react';

import Colors from '../constants/Colors';
import useColorScheme from '../hooks/useColorScheme';
import AppAnalytics from '../lib/analytics/Analytics';
import ArtistDashboardScreen from '../screens/ArtistDashboard';
import ArtistSessionResultsScreen from '../screens/ArtistSessionResults';
import ArtistProfileScreen from '../screens/ArtistProfile';
import ArtistCreateProfileScreen from '../screens/ArtistCreateProfile';
import ArtistGenresScreen from '../screens/ArtistGenres';
import CreateSessionScreen from '../screens/CreateSession';
import LogoAndLogout from '../components/styled/LogoAndLogout';
import SessionGenresScreen from '../screens/SessionGenres';
import {
  ArtistTabParamList,
  ArtistDashboardParamList,
  ArtistProfileParamList,
} from '../types/types';

const BottomTab = createBottomTabNavigator<ArtistTabParamList>();

export default function ArtistTabNavigator() {
  const colorScheme = useColorScheme();

  return (
    <BottomTab.Navigator
      initialRouteName="Dashboard"
      tabBarOptions={{ activeTintColor: Colors[colorScheme].tint }}
    >
      <BottomTab.Screen
        name="Dashboard"
        component={ArtistDashboardNavigator}
        options={{
          tabBarIcon: ({ color }) => <TabBarIcon name="home" color={color} />,
        }}
      />
      <BottomTab.Screen
        name="Profile"
        component={ArtistProfileNavigator}
        options={{
          tabBarIcon: ({ color }) => <TabBarIcon name="user-o" color={color} />,
        }}
      />
    </BottomTab.Navigator>
  );
}

// You can explore the built-in icon families and icons on the web at:
// https://icons.expo.fyi/
function TabBarIcon(props: { name: string; color: string }) {
  const { color, name } = props;
  return (
    <FontAwesome
      color={color}
      name={name}
      size={25}
      style={{ marginBottom: -3 }}
    />
  );
}

// Each tab has its own navigation stack, you can read more about this pattern here:
// https://reactnavigation.org/docs/tab-based-navigation#a-stack-navigator-for-each-tab
const DashboardStack = createStackNavigator<ArtistDashboardParamList>();

function ArtistDashboardNavigator() {
  return (
    <DashboardStack.Navigator
      screenOptions={{ headerRight: () => <LogoAndLogout /> }}
    >
      <DashboardStack.Screen
        name="Dashboard"
        component={ArtistDashboardScreen}
        options={{
          headerTitle: 'Dashboard',
        }}
        listeners={({ route }) => ({
          focus: () => AppAnalytics.sendPageHit(route.name),
        })}
      />
      <DashboardStack.Screen
        name="ArtistSessionResults"
        component={ArtistSessionResultsScreen}
        options={{
          headerTitle: 'Session Results',
        }}
        listeners={({ route }) => ({
          focus: () => AppAnalytics.sendPageHit(route.name),
        })}
      />
      <DashboardStack.Screen
        name="CreateSession"
        component={CreateSessionScreen}
        options={{
          headerTitle: 'Create Session',
        }}
        listeners={({ route }) => ({
          focus: () => AppAnalytics.sendPageHit(route.name),
        })}
      />
      <DashboardStack.Screen
        name="SessionGenres"
        component={SessionGenresScreen}
        options={{
          headerTitle: 'Session Genres',
        }}
        listeners={({ route }) => ({
          focus: () => AppAnalytics.sendPageHit(route.name),
        })}
      />
    </DashboardStack.Navigator>
  );
}

const ProfileStack = createStackNavigator<ArtistProfileParamList>();

function ArtistProfileNavigator() {
  return (
    <ProfileStack.Navigator
      screenOptions={{ headerRight: () => <LogoAndLogout /> }}
    >
      <ProfileStack.Screen
        name="Profile"
        component={ArtistProfileScreen}
        options={{
          headerTitle: 'Profile',
        }}
        listeners={({ route }) => ({
          focus: () => AppAnalytics.sendPageHit(route.name),
        })}
      />
      <ProfileStack.Screen
        name="ArtistCreateProfile"
        component={ArtistCreateProfileScreen}
        options={{
          headerTitle: 'Create Your Profile',
        }}
        listeners={({ route }) => ({
          focus: () => AppAnalytics.sendPageHit(route.name),
        })}
      />
      <ProfileStack.Screen
        name="ArtistGenres"
        component={ArtistGenresScreen}
        options={{
          headerTitle: 'Artist Genres',
        }}
        listeners={({ route }) => ({
          focus: () => AppAnalytics.sendPageHit(route.name),
        })}
      />
    </ProfileStack.Navigator>
  );
}
